import React, { useEffect, useState } from 'react';
import useTable from '../../hooks/useTable';
import { Typography, SelectChangeEvent } from '@mui/material';
import ReportsTable from '../dashboard/components/reports-components/ReportsTable';
import StyledTablePaper from '../dashboard/styled/StyledTablePaper';
import JobHistoryFilters from './components/JobHistoryFilters';
import StyledRow from './styled/StyledRow';
import StyledTypography from './styled/StyledTypography';
import StyledButton from './styled/StyledButton';
import IosShareIcon from '@mui/icons-material/IosShare';
import BkDropdownMenu from '../../components/dropdown-menu/BkDropdownMenu';
import { DashboardFilters } from '../../utils/constant';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store/store';
import { formatDateForFiltering } from 'src/utils/utility-functions';
import BkSnackBar from 'src/components/snackbar/BkSnackBar';

const STATUSES = [
  {
    label: 'In Queue',
    id: 'in_queue',
  },
  {
    label: 'Processing',
    id: 'processing',
  },
  {
    label: 'Printing',
    id: 'printing',
  },
  {
    label: 'Complete',
    id: 'completed',
  },
  {
    label: 'On Hold',
    id: 'on_hold',
  },
  {
    label: 'Cancelled',
    id: 'cancelled',
  },
];

const NOTICE_TYPES = [
  {
    label: 'Bankrupcty',
    id: 'bankruptcy notice',
  },
  {
    label: 'Legal',
    id: 'legal',
  },
  {
    label: 'Direct',
    id: 'direct mailing',
  },
];

const PRINT_GEN_STATUSES = [
  {
    label: 'Not Started',
    id: 'not_started',
  },
  {
    label: 'In Process',
    id: 'in_process',
  },
  {
    label: 'Complete',
    id: 'complete',
  },
];

const SERVICE_OPTIONS = [
  { label: 'Normal', id: 'normal' },
  { label: 'Rush', id: 'rush' },
];

const JobHistory = () => {
  const [selectedFilters, setSelectedFilters] = useState<DashboardFilters>({
    noticeType: '',
    status: [],
    createdAt_start: '',
    createdAt_end: '',
    searchValue: '',
    onHoldReason: [],
    printGenStatus: '',
    service: [],
  });

  const [searchTerm, setSearchTerm] = useState(selectedFilters.searchValue);

  const userRole = useSelector((state: RootState) => state.user.currentUser.role);
  const teamRole = useSelector((state: RootState) => state.user.currentUser.teamRole);

  useEffect(() => {
    const handler = setTimeout(() => {
      setSelectedFilters((prevFilters) => ({
        ...prevFilters,
        searchValue: searchTerm,
      }));
      setPage(0);
    }, 1000);

    return () => clearTimeout(handler);
  }, [searchTerm]); // debounce search value

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterChange = (event: SelectChangeEvent<string | (string | null)[]>) => {
    if (Array.isArray(event)) {
      setSelectedFilters((prevFilters) => ({
        ...prevFilters,
        createdAt_start: (event as any)[0] ? formatDateForFiltering(event[0] as any, 'start') : '',
        createdAt_end: (event as any)[1] ? formatDateForFiltering(event[1] as any, 'end') : '',
      }));
    } else {
      const { name, value } = event.target;

      setSelectedFilters((prevFilters) => ({
        ...prevFilters,
        [name]: value,
      }));
    }
    setPage(0);
  };

  const {
    data,
    error,
    loading,
    exportAnchor,
    exportItems,
    errorMsg,
    clearErrorMsg,
    openExportButton,
    closeExportButton,
    exportData,
    updateData,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    totalCount,
  } = useTable({
    activityType: 'history',
    filters: selectedFilters,
    role: userRole,
  });

  return (
    <StyledTablePaper length={data.length} elevation={0}>
      <BkSnackBar open={!!errorMsg} handleCloseSnackbar={clearErrorMsg} snackMessage={errorMsg} severity="error" />
      <StyledRow marginBottom="1.875rem">
        <Typography variant="h4" sx={{ fontSize: '3.25rem', fontFamily: 'ABC Arizona Mix', fontWeight: 300 }}>
          Job History
        </Typography>
      </StyledRow>
      <StyledRow marginBottom="0.9375rem">
        <JobHistoryFilters
          noticeTypes={NOTICE_TYPES}
          statuses={STATUSES}
          printGenStatuses={PRINT_GEN_STATUSES}
          serviceOptions={SERVICE_OPTIONS}
          onFilterChange={handleFilterChange}
          selectedFilters={selectedFilters}
          handleSearchChange={handleSearchChange}
          searchTerm={searchTerm}
          isCosMember={teamRole === 'admin' || teamRole === 'support'}
        />
        <StyledButton
          variant="contained"
          disableElevation
          startIcon={<IosShareIcon fontSize="small" />}
          onClick={openExportButton}
        >
          Export
        </StyledButton>
        <BkDropdownMenu
          anchorEl={exportAnchor}
          open={!!exportAnchor}
          onClose={closeExportButton}
          menuItems={exportItems}
        />
      </StyledRow>
      {loading ? (
        <StyledTypography>Loading...</StyledTypography>
      ) : error ? (
        <StyledTypography>Something Went Wrong</StyledTypography>
      ) : data.length > 0 ? (
        <ReportsTable
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          totalCount={totalCount}
          updateData={updateData}
          showUserColumn={userRole === 'admin'}
          showPagination={true}
          data={data}
        />
      ) : (
        <StyledTypography>No jobs available</StyledTypography>
      )}
    </StyledTablePaper>
  );
};

export default JobHistory;
